import { Box, Typography } from '@mui/material';
import { useQueryClient } from 'react-query';
import { useMemo } from 'react';

// Our Components
import ConfirmArchive from 'components/Modal/KeyActions/ConfirmKeyActionChange';
import TradelineContent from 'components/Tradeline/TradelineContent';
import KeyActionContent from 'components/Cards/KeyActions/KeyActionContent';
import TasksAccordion from 'components/Tasks/TasksAccordion';

// Our Hooks
import useMutateSendEmail from 'hooks/email/useMutateSendEmail';
import useMutateChangeKeyActionStatus from 'hooks/keyActions/useMutateChangeKeyActionStatus';

// Our Query Keys
import { SUCCESS_MESSAGE_DATA } from 'shared/query-keys';

// Our Utils
import formatKeyActionName from 'shared/utils/formatting/keyaction/formatKeyActionName';

function MortgageRefiKeyAction({
	isOpen,
	handleClose,
	setSuccessAlert,
	keyActionInfo
}) {
	const triggerAction = useMutateSendEmail();
	const toggleStatus = useMutateChangeKeyActionStatus();
	const queryClient = useQueryClient();

	const {
		keyActionName,
		advisorFirstName,
		advisorLastName,
		advisorEmail,
		clientFirstName,
		clientLastName,
		sentDate,
		keyActionQualification,
		tradelineId
	} = keyActionInfo;

	const isSent = sentDate !== null;

	const subtitle = isSent
		? `This action was last sent on ${sentDate}.`
		: 'This action has not been sent yet.';

	const formattedKeyActionName = useMemo(
		() => formatKeyActionName(keyActionName),
		[keyActionName]
	);

	return (
		<ConfirmArchive
			isOpen={isOpen}
			handleClose={handleClose}
			title={`Key Action - ${formattedKeyActionName} for ${clientFirstName} ${clientLastName}`}
			subtitle={subtitle}
			primaryButtonText="Trigger Key Action"
			confirmButtonCallback={() => {
				const toggleStatusPayload = {
					id: keyActionInfo.id,
					status: 'SENT'
				};

				toggleStatus.mutate(toggleStatusPayload, {
					onSuccess: () => {
						triggerAction.mutate(
							{
								templateName: 'KeyActions_Mortgage_Refi_1',
								emailSubject: 'Mortgage Refinance Alert',
								emailRecepientList: [advisorEmail],
								emailVarArgs: {
									advisorFullName: `${advisorFirstName} ${advisorLastName}`,
									clientFullName: `${clientFirstName} ${clientLastName}`,
									keyActionName: 'Mortgage Refinance Alert'
								}
							},
							{
								onSuccess: () => {
									queryClient.setQueryData(
										SUCCESS_MESSAGE_DATA,
										'Key Action Triggered'
									);
									handleClose();
									setSuccessAlert(true);
								}
							}
						);
					}
				});
			}}
			isConfirmButtonLoading={triggerAction.isLoading}
			isConfirmButtonError={triggerAction.isError}
		>
			<TasksAccordion taskHeading="View Trade Line Data">
				<TradelineContent tradelineId={tradelineId} />
			</TasksAccordion>
			<TasksAccordion
				taskHeading="Key Action Qualifications"
				defaultExpandState
			>
				<KeyActionContent
					withHeading={false}
					keyActionQualification={keyActionQualification}
				/>
			</TasksAccordion>

			<Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
				<Typography>
					Are you sure you want to trigger this key action?
				</Typography>
			</Box>
		</ConfirmArchive>
	);
}

export default MortgageRefiKeyAction;
